import React, { useEffect, useState } from 'react';
import { HiSparkles } from 'react-icons/hi';
import Button from '../Button';
import {
  dangerNotification,
} from '../../services/notification';
import {
  FollowUpRewriteContext,
  rewriteFollowUp,
} from '../../services/followup-rewrite';
import { canUseFollowUpRewrite } from '../../utils/canUseFollowUpRewrite';
import { hasEditorText } from '../../utils/hasEditorText';

export type FollowUpRewriteActionsProps = {
  message: string;
  onChange: (message: string) => void;
  context?: FollowUpRewriteContext | string;
  disabled?: boolean;
};

const FollowUpRewriteActions = ({
  message,
  onChange,
  context,
  disabled = false,
}: FollowUpRewriteActionsProps) => {
  const [loading, setLoading] = useState(false);
  const [originalMessage, setOriginalMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!hasEditorText(message)) {
      setOriginalMessage(null);
    }
  }, [message]);

  if (!canUseFollowUpRewrite()) {
    return null;
  }

  const isDisabled = disabled || loading || !hasEditorText(message);

  const handleRewrite = async () => {
    if (isDisabled) {
      return;
    }

    const currentMessage = message;
    setOriginalMessage(previous => previous || currentMessage);
    setLoading(true);

    try {
      const response = await rewriteFollowUp(currentMessage, context);
      if (!response?.message) {
        throw new Error('A IA não retornou uma sugestão.');
      }
      onChange(response.message);
    } catch (error) {
      const apiError = error as { message?: string; data?: { message?: string } };
      dangerNotification(
        'Não foi possível reescrever',
        apiError?.message ||
          apiError?.data?.message ||
          'Tente novamente em instantes.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUseOriginal = () => {
    if (originalMessage === null || loading) {
      return;
    }
    onChange(originalMessage);
    setOriginalMessage(null);
  };

  return (
    <>
      <Button
        type="button"
        light
        onClick={handleRewrite}
        loading={loading}
        disabled={isDisabled}
        title="Sugerir uma reescrita com IA"
        aria-label="Reescrever com IA"
      >
        <HiSparkles style={{ marginRight: 6, verticalAlign: 'middle' }} />
        Reescrever
      </Button>
      {originalMessage !== null && (
        <Button
          type="button"
          light
          onClick={handleUseOriginal}
          disabled={loading}
          title="Voltar ao texto original"
        >
          Usar texto original
        </Button>
      )}
    </>
  );
};

export default FollowUpRewriteActions;
